import {
  CONDITION_CODES,
  EXTENSIONS_SELF_REPORTED,
  EXTENSION_URL,
} from '@constants/extension-codes';

import {
  MedicalConditionRecord,
  type ConditionValues,
} from '@models/medical-condition';

import { formatPatientId } from '@utils/format';

import type { ConditionModelInput, Extension } from 'generated/types';
import {
  ConditionClinicalStatusCode,
  ConditionVerificationStatusCode,
} from 'generated/types';

export const getMedicalConditionTemplate = (
  patient_id: string,
  values: ConditionValues
): ConditionModelInput => {
  const extensions: Extension[] = [];
  if (values.snomedCode || values.icd10Code || values.vitallCode) {
    extensions.push({
      url: `${EXTENSION_URL}${CONDITION_CODES}`,
      valueCodeableConcept: {
        coding: [
          ...(values.snomedCode
            ? [
                {
                  display: values.snomedCode,
                  system: MedicalConditionRecord.SNOMED_SYSTEM,
                },
              ]
            : []),
          ...(values.icd10Code
            ? [
                {
                  display: values.icd10Code,
                  system: MedicalConditionRecord.ICD_10_SYSTEM,
                },
              ]
            : []),
          ...(values.vitallCode
            ? [
                {
                  display: values.vitallCode,
                  system: MedicalConditionRecord.VITALL_SYSTEM,
                },
              ]
            : []),
        ],
        text: CONDITION_CODES,
      },
    });
  }
  if (values.selfReported) {
    extensions.push({
      url: `${EXTENSION_URL}${EXTENSIONS_SELF_REPORTED}`,
      valueBoolean: values.selfReported,
    });
  }
  if (values.associatedResources) {
    values.associatedResources.forEach((resource) => {
      extensions.push({
        url: 'https://vitall.com/documentation/extensions/associated-records',
        valueReference: {
          reference: resource,
        },
      });
    });
  }

  return {
    resourceType: 'Condition',
    subject: {
      reference: formatPatientId(patient_id),
    },
    ...(values.category && {
      category: [
        {
          text: values.category,
        },
      ],
    }),
    clinicalStatus: {
      coding: [
        {
          code:
            values.clinicalStatus === 'inactive'
              ? ConditionClinicalStatusCode.inactive
              : ConditionClinicalStatusCode.active,
          display: values.clinicalStatus === 'inactive' ? 'Inactive' : 'Active',
          system: 'http://terminology.hl7.org/CodeSystem/condition-clinical',
        },
      ],
      text: values.clinicalStatus === 'inactive' ? 'Inactive' : 'Active',
    },
    verificationStatus: {
      coding: [
        {
          code: ConditionVerificationStatusCode.confirmed,
          display: 'Confirmed',
          system: 'http://terminology.hl7.org/CodeSystem/condition-ver-status',
        },
      ],
      text: 'Confirmed',
    },
    code: {
      text: values.title,
    },
    ...(values.onsetDateTime && {
      onsetDateTime: values.onsetDateTime,
    }),
    ...(extensions.length > 0 && {
      extension: extensions,
    }),
  };
};
